// Formatos de la nota de venta (VentaDirecta y ListadoVentas). Al imprimir se escoge:
//  - "cambios": política de cambios (30 días, con ticket, mismo modelo, hasta 20%).
//  - "pagare":  pagaré con el total de la nota + "No se permiten devoluciones".
import Swal from "sweetalert2";

export const EMPRESA_NOTA = {
  razonSocial: "COSECHANDO BENDICIONES Y EXITOS SA DE CV",
  ciudad: "Ciudad de México",
};

export const LEYENDA_CAMBIOS =
  "CAMBIOS: 30 días con su ticket de compra, solo por modelo. Se cambia hasta el 20% de la mercancía, " +
  "con su ticket de compra, y solo por el mismo modelo.";

// Pregunta el formato. Devuelve "cambios" | "pagare" | null (si cancelan).
export async function elegirFormatoNota() {
  const r = await Swal.fire({
    title: "¿Qué formato de nota?",
    input: "radio",
    inputOptions: {
      cambios: "Con cambios (30 días)",
      pagare: "Pagaré (sin devoluciones)",
    },
    inputValue: "cambios",
    showCancelButton: true,
    confirmButtonText: "Imprimir",
    cancelButtonText: "Cancelar",
  });
  return r.isConfirmed ? r.value : null;
}

// ---------- Número a letras (pesos mexicanos) ----------
const UNIDADES = ["", "UN", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE"];
const ESPECIALES = {
  10: "DIEZ", 11: "ONCE", 12: "DOCE", 13: "TRECE", 14: "CATORCE", 15: "QUINCE",
  16: "DIECISEIS", 17: "DIECISIETE", 18: "DIECIOCHO", 19: "DIECINUEVE",
  20: "VEINTE", 21: "VEINTIUN", 22: "VEINTIDOS", 23: "VEINTITRES", 24: "VEINTICUATRO",
  25: "VEINTICINCO", 26: "VEINTISEIS", 27: "VEINTISIETE", 28: "VEINTIOCHO", 29: "VEINTINUEVE",
};
const DECENAS = ["", "", "", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
const CENTENAS = ["", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS",
  "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS"];

function menorMil(n) {
  if (n === 0) return "";
  if (n === 100) return "CIEN";
  const c = Math.floor(n / 100), r = n % 100;
  let t = "";
  if (r < 10) t = UNIDADES[r];
  else if (r < 30) t = ESPECIALES[r];
  else t = DECENAS[Math.floor(r / 10)] + (r % 10 ? " Y " + UNIDADES[r % 10] : "");
  return [CENTENAS[c], t].filter(Boolean).join(" ");
}

export function numeroALetras(monto) {
  const total = Math.round((Number(monto) || 0) * 100) / 100;
  const entero = Math.floor(total);
  const centavos = Math.round((total - entero) * 100);
  const millones = Math.floor(entero / 1e6);
  const miles = Math.floor((entero % 1e6) / 1000);
  const resto = entero % 1000;
  const partes = [];
  if (millones) partes.push(millones === 1 ? "UN MILLON" : `${menorMil(millones)} MILLONES`);
  if (miles) partes.push(miles === 1 ? "MIL" : `${menorMil(miles)} MIL`);
  if (resto) partes.push(menorMil(resto));
  const letras = partes.length ? partes.join(" ") : "CERO";
  const de = !resto && !miles && millones ? " DE" : ""; // "UN MILLON DE PESOS"
  return `${letras}${de} ${entero === 1 ? "PESO" : "PESOS"} ${String(centavos).padStart(2, "0")}/100 M.N.`;
}

const money = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n) || 0);

const sumaDias = (fechaISO, dias) => {
  const d = new Date(`${fechaISO}T12:00:00`);
  if (isNaN(d)) return fechaISO || "";
  d.setDate(d.getDate() + (Number(dias) || 0));
  return d.toISOString().slice(0, 10);
};

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto",
  "septiembre", "octubre", "noviembre", "diciembre"];
const fechaLarga = (iso) => {
  const [a, m, d] = String(iso || "").split("-").map(Number);
  return a && m && d ? `${d} de ${MESES[m - 1]} de ${a}` : String(iso || "");
};

// Si no cabe 'alto' mm desde 'y', pasa a hoja nueva
const asegura = (doc, y, alto) => {
  const H = doc.internal.pageSize.getHeight();
  if (y + alto > H - 10) {
    doc.addPage();
    return 15;
  }
  return y;
};

/**
 * Dibuja el pie de la nota según el formato y devuelve la nueva Y.
 * datos: { numero, fecha (YYYY-MM-DD), total, plazo (días), cliente, domicilio }
 */
export function agregarPieNota(doc, formato, y, datos = {}, copia = false) {
  const W = doc.internal.pageSize.getWidth();
  const x = 12, ancho = W - 24;
  doc.setTextColor(0, 0, 0);

  // La COPIA (pedido pendiente de facturar) nunca lleva el pagaré
  if (formato === "pagare" && copia) {
    y = asegura(doc, y + 4, 8);
    doc.setFont(undefined, "bold");
    doc.setFontSize(11);
    doc.text("NO SE PERMITEN DEVOLUCIONES.", x, y + 2);
    doc.setFont(undefined, "normal");
    return y + 8;
  }

  if (formato === "pagare") {
    const vence = sumaDias(datos.fecha, datos.plazo);
    const cuerpo =
      `Debo(emos) y pagaré(mos) incondicionalmente por este pagaré a la orden de ${EMPRESA_NOTA.razonSocial}, ` +
      `en ${EMPRESA_NOTA.ciudad}, el día ${fechaLarga(vence)}, la cantidad de ${money(datos.total)} ` +
      `(${numeroALetras(datos.total)}), valor recibido en mercancía a mi (nuestra) entera satisfacción. ` +
      `Desde la fecha de su vencimiento hasta el día de su liquidación causará intereses moratorios al tipo de ` +
      `______% mensual, pagadero en esta ciudad juntamente con el principal.`;
    doc.setFontSize(9);
    const lineas = doc.splitTextToSize(cuerpo, ancho - 6);
    const alto = 14 + lineas.length * 4.2 + 26;
    y = asegura(doc, y + 4, alto);
    doc.setDrawColor(60);
    doc.rect(x, y, ancho, alto);
    doc.setFont(undefined, "bold");
    doc.setFontSize(12);
    doc.text("PAGARÉ", x + 3, y + 7);
    doc.setFontSize(10);
    doc.text(`No. ${datos.numero || ""}`, x + ancho / 2, y + 7, { align: "center" });
    doc.text(`Bueno por: ${money(datos.total)}`, x + ancho - 3, y + 7, { align: "right" });
    doc.setFont(undefined, "normal");
    doc.setFontSize(9);
    doc.text(`En ${EMPRESA_NOTA.ciudad}, a ${fechaLarga(datos.fecha)}.`, x + 3, y + 13);
    doc.text(lineas, x + 3, y + 18.5);
    let yf = y + 18.5 + lineas.length * 4.2 + 4;
    doc.text(`Nombre: ${datos.cliente || ""}`, x + 3, yf);
    const dom = doc.splitTextToSize(`Domicilio: ${datos.domicilio || "________________________________"}`, ancho * 0.62);
    doc.text(dom, x + 3, yf + 5);
    doc.line(x + ancho - 62, yf + 12, x + ancho - 6, yf + 12);
    doc.text("Firma de aceptación", x + ancho - 34, yf + 16, { align: "center" });
    y = y + alto + 6;
    y = asegura(doc, y, 8);
    doc.setFont(undefined, "bold");
    doc.setFontSize(11);
    doc.text("NO SE PERMITEN DEVOLUCIONES.", x, y);
    doc.setFont(undefined, "normal");
    return y + 6;
  }

  // Con cambios
  doc.setFontSize(10);
  doc.setFont(undefined, "bold");
  const lineas = doc.splitTextToSize(LEYENDA_CAMBIOS, ancho);
  y = asegura(doc, y + 2, lineas.length * 5 + 4);
  doc.text(lineas, x, y + 4);
  doc.setFont(undefined, "normal");
  return y + 4 + lineas.length * 5;
}

// ¿La venta todavía no tiene factura? (numeroFactura "No", vacío o NA)
export const pendienteDeFacturar = (numeroFactura) => {
  const f = String(numeroFactura == null ? "" : numeroFactura).trim().toUpperCase();
  return !f || f === "NO" || f === "NA" || f === "0";
};

// Aviso de la nota (original y copia): pedido pendiente de facturar hasta que el cliente lo confirme
export const AVISO_PENDIENTE_FACTURAR = {
  titulo: "PEDIDO PENDIENTE DE FACTURAR",
  texto:
    "Este pedido queda pendiente de facturar hasta que el cliente lo confirme. " +
    "Una vez confirmado por el cliente, se emitirá la factura correspondiente.",
};
export const AVISO_COPIA_PEDIDO = AVISO_PENDIENTE_FACTURAR;

// Rótulo arriba a la derecha de cada hoja: "ORIGINAL — CLIENTE" o "COPIA — PEDIDO PENDIENTE DE FACTURAR"
export function rotuloCopia(doc, copia) {
  const W = doc.internal.pageSize.getWidth();
  const txt = copia ? "COPIA — PEDIDO PENDIENTE DE FACTURAR" : "ORIGINAL — CLIENTE";
  doc.setFont(undefined, "bold");
  doc.setFontSize(8);
  const w = doc.getTextWidth(txt) + 6;
  if (copia) {
    doc.setFillColor(146, 64, 14);
    doc.setTextColor(255, 255, 255);
  } else {
    doc.setFillColor(229, 231, 235);
    doc.setTextColor(31, 41, 55);
  }
  doc.roundedRect(W - 12 - w, 3, w, 5.5, 1.5, 1.5, "F");
  doc.text(txt, W - 12 - w / 2, 6.8, { align: "center" });
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, "normal");
}

/**
 * Recuadro "Mercancía pendiente de facturar" (fondo amarillo claro, título en negritas).
 * Devuelve la nueva Y. 'numero' (opcional) se menciona en el texto.
 */
export function agregarAvisoPendienteFacturar(doc, y, numero, copia = false) {
  const W = doc.internal.pageSize.getWidth();
  const x = 12, ancho = W - 24;
  doc.setFontSize(9);
  doc.setFont(undefined, "normal");
  const aviso = copia ? AVISO_COPIA_PEDIDO : AVISO_PENDIENTE_FACTURAR;
  const texto = numero ? aviso.texto.replace("su número de nota", `su número de nota (#${numero})`) : aviso.texto;
  const lineas = doc.splitTextToSize(texto, ancho - 8);
  const alto = 10 + lineas.length * 4.2 + 3;
  y = asegura(doc, y + 4, alto);
  doc.setFillColor(255, 247, 214);
  doc.setDrawColor(217, 160, 0);
  doc.setLineWidth(0.5);
  doc.roundedRect(x, y, ancho, alto, 2, 2, "FD");
  doc.setLineWidth(0.2);
  doc.setTextColor(146, 64, 14);
  doc.setFont(undefined, "bold");
  doc.setFontSize(11);
  doc.text(aviso.titulo, x + 4, y + 6.5);
  doc.setFont(undefined, "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(lineas, x + 4, y + 11.5);
  doc.setTextColor(0, 0, 0);
  return y + alto + 2;
}

// Espacio para que el cliente confirme el pedido (firma). Devuelve la nueva Y.
export function firmaConfirmacionPedido(doc, y) {
  const W = doc.internal.pageSize.getWidth();
  const x = 12, ancho = W - 24;
  y = asegura(doc, y + 6, 22);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.setFontSize(9);
  doc.setFont(undefined, "bold");
  doc.text("CONFIRMACIÓN DEL PEDIDO", x, y);
  doc.setFont(undefined, "normal");
  doc.text("Confirmo este pedido y autorizo su facturación.", x, y + 5);
  doc.line(x + ancho - 75, y + 14, x + ancho - 5, y + 14);
  doc.text("Nombre y firma del cliente", x + ancho - 40, y + 18, { align: "center" });
  doc.line(x, y + 14, x + 50, y + 14);
  doc.text("Fecha", x + 25, y + 18, { align: "center" });
  return y + 22;
}

// Domicilio de un cliente del catálogo (para el pagaré)
export function domicilioCliente(c) {
  if (!c) return "";
  const v = (s) => (s && s !== "NA" ? String(s).trim() : "");
  const calle = [v(c.calle), v(c.numero_ext) && `#${v(c.numero_ext)}`, v(c.numero_int) && `Int. ${v(c.numero_int)}`]
    .filter(Boolean)
    .join(" ");
  return [calle, v(c.colonia) && `Col. ${v(c.colonia)}`, v(c.cp) && v(c.cp) !== "0" && `CP ${v(c.cp)}`,
    v(c.delegacion), v(c.ciudad), v(c.estado)]
    .filter(Boolean)
    .join(", ");
}
